import Box from "@mui/material/Box";
import { HELP_MODULE_FAQ_QID_KEY } from "../../config/common";
import React from "react";
import { TOKEN_HOSTNAME_ENV } from "../../config/tokens";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { replaceEnvTokenInUrl } from "../../config/environment";
import { styled } from "@mui/material/styles";
import useNavigateQSA from "../../utils/hooks/useNavigateQSA";
import { useTheme } from "@mui/material";

const TableContainerHelp = styled(TableContainer)(({ theme }) => ({
	margin: "12px 0"
}));

const TableCellHelpHead = styled(TableCell)(({ theme }) => ({
	color: theme.palette.primary.contrastText,
	fontSize: theme.typography.fontSize,
	fontWeight: theme.typography.fontWeightBold,
	paddingTop: 12
}));

const TableCellHelpBody = styled(TableCell)(({ theme }) => ({
	color: theme.palette.primary.contrastText,
}));

const HelpModuleFAQHeader = props => {
	return (
		<Typography variant="h1">
			Frequently Asked Questions
		</Typography>
	);
};

const HelpModuleFAQItem = props => {

	const {
		children,
		questionId,
		title
	} = props;

	const theme = useTheme();
	const queryParams = new URLSearchParams(window.location.search);
	const activeQuestionId = queryParams.get(HELP_MODULE_FAQ_QID_KEY);
	const navigate = useNavigateQSA();
	const isActiveQuestionId = (questionId === activeQuestionId || activeQuestionId === "all");

	React.useEffect(() => {
		if (isActiveQuestionId && activeQuestionId !== "all") {
			const element = document.querySelector(`button[value="${activeQuestionId}"]`);
			element.scrollIntoView(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dtStyles = {
		alignItems: "center",
		borderTop: "1px solid #929292",
		display: "flex",

		"& > button": {
			alignItems: "center",
			background: "none",
			border: "none",
			color: theme.palette.primary.contrastText,
			cursor: "pointer",
			display: "flex",
			flex: "1 1 auto",
			fontFamily: theme.typography.fontFamily,
			fontSize: theme.typography.fontSize,
			fontWeight: (isActiveQuestionId ? 700 : 400),
			height: 50,
			justifyContent: "flex-start",
			padding: 0,
			textAlign: "left",

			"&::after": {
				background: `url(${process.env.PUBLIC_URL}/assets/general/icon-arrow.svg) no-repeat center center`,
				content: "''",
				display: "block",
				flex: "0 0 auto",
				height: "10px",
				marginLeft: "auto",
				transform: `rotate(${(isActiveQuestionId ? 180 : 0)}deg)`,
				width: "37px"
			}
		},
	};
	const ddStyles = {
		paddingBottom: 2
	};

	const handleClick = e => {
		e.preventDefault();
		const { value } = e.target;
		navigate({ search: { [HELP_MODULE_FAQ_QID_KEY]: (activeQuestionId === value ? null : value) } });
	};

	return (
		<React.Fragment>
			<Box component="dt" sx={dtStyles}>
				<Box
					component="button"
					onClick={handleClick}
					value={questionId}
				>
					{title}
				</Box>
			</Box>
			{isActiveQuestionId && (
				<Box
					component="dd"
					sx={ddStyles}
				>
					{children}
				</Box>
			)}
		</React.Fragment>
	);
};

const HelpModuleFAQ = props => {

	return (
		<React.Fragment>
			<Typography component="h2" variant="h2" gutterBottom>General FAQ</Typography>
			<Typography className="cmp-help-module__faq" component="dl" mb={3}>
				<HelpModuleFAQItem
					questionId="01"
					title="What does Default mean?"
				>
					<Typography>
						Default on a Federal Family Education Loan Program (FFELP) loan occurs when you fail to
						make payments and your loan reaches 270 days of delinquency. When your Federal student
						loan reaches 270 days delinquent, the lender of your loan(s) submits a claim to the
						guarantor (PHEAA) to purchase the loan(s).
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="02"
					title="What should I do if my student loan Defaults?"
				>
					<Typography>
						Contact us immediately at 1-800-233-0751 to find out what repayment options are available
						and to avoid additional consequences.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="03"
					title="How do I contact PHEAA?"
				>
					<Typography>
						<a href={replaceEnvTokenInUrl(`https://www${TOKEN_HOSTNAME_ENV}pheaa.org/about/contact-us/default-collections.shtml`)}>Contact us</a> to find out what repayment options are available and to avoid additional consequences.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="04"
					title="What are my repayment options?"
				>
					<Typography component="ul">
						<Typography component="li">
							<strong>Paying the loan in full</strong> - This is your best option. PHEAA allows you up to 75 days from the
							date of default to pay off the defaulted loan balance without any consequences, such as collection costs and
							negative credit reporting while your loan is in default.
						</Typography>
						<Typography component="li">
							<strong>Monthly payments</strong> - If you are unable to make the full defaulted balance, PHEAA will offer
							monthly payments. By making monthly payments in a timely manner, you may qualify to participate in the Loan
							Rehabilitation Program.
						</Typography>
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="05"
					title="Is PHEAA part of the U.S. Department of Education (The Department)?"
				>
					<Typography>
						PHEAA is not part of the Department; however, PHEAA is affiliated with and administers the federally regulated
						programs that the Department provides.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="06"
					title="What is the Loan Rehabilitation Program?"
				>
					<Typography>
						The Loan Rehabilitation Program offers a defaulted borrower the opportunity to return their loan(s) to good standing.
						Upon completing the Loan Rehabilitation Program, you may be eligible for applicable deferments, forbearances, and
						repayment options. In order to successfully complete the Loan Rehabilitation Program, you must adhere to the
						requirements listed below:
					</Typography>
					<Typography component="ul">
						<Typography component="li">
							You are required to make nine qualifying monthly payments within a 10-month period. A qualifying payment is:
							<Typography component="ul">
								<Typography component="li">
									A payment that is made voluntary. Payments received through garnishment or federal offset do not qualify;
								</Typography>
								<Typography component="li">
									A payment that is received on-time. On-time is within 20 days of the due date for the payment; and
								</Typography>
								<Typography component="li">
									The full amount required. You cannot pay a lump sum amount or make double payments to qualify sooner in the
									10-month period. Remember that you are demonstrating the ability to make your monthly payments after the
									default status has been removed from your loan.
								</Typography>
							</Typography>
						</Typography>
						<Typography component="li">
							You must make your entire monthly payment on-time each month until you receive your Rehabilitation Approval Notice.
							Contact us immediately if you have any questions regarding whether or not you should make a payment.
						</Typography>
						<Typography component="li">
							You must have a minimum principal and interest balance of $50 at the time of rehabilitation (after you make your nine qualifying payments).
						</Typography>
					</Typography>
					<Typography gutterBottom={true}>
						Please note, successful completion of the rehabilitation program requires that PHEAA have a lender available to
						purchase your loan(s). If PHEAA is unable to secure a lender, payments must continue to be made to maintain
						eligibility until such time as a lender is available to complete the rehabilitation.
					</Typography>
					<Typography>
						Also, any loan(s) previously rehabilitated on or after 8/14/2008 do not qualify for this program.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="07"
					title="How do I qualify for the Loan Rehabilitation Program?"
				>
					<Typography>
						To obtain information concerning the Loan Rehabilitation Program and to determine if you qualify, please contact us
						directly at 1-800-233-0751. Our office hours are Monday through Friday 8:00 AM to 5:00 PM (ET). When contacting us,
						please have your most recent federal tax return available, if possible, as your required payment for this program will
						be calculated based upon your Adjusted Gross Income (AGI) and family size or the completion of a Financial Disclosure Form.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="08"
					title="What are the benefits of the Loan Rehabilitation Program?"
				>
					<Typography>After successfully completing the Loan Rehabilitation Program:</Typography>
					<Typography component="ul">
						<Typography component="li">
							We will request that the consumer reporting agencies remove any negative credit reporting for your loan while it was in default.
							However, negative information reported by your loan servicer prior to default will not be removed as a result of rehabilitation.
						</Typography>
						<Typography component="li">You may be eligible for applicable deferments, forbearances and repayment options.</Typography>
						<Typography component="li">You will regain eligibility for future federal financial aid.</Typography>
					</Typography>
					<Typography>Please note, you may only complete the Loan Rehabilitation Program one time.</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="09"
					title="What if I previously completed the Loan Rehabilitation Program, but I defaulted on my loans again?"
				>
					<Typography>
						Unfortunately, you are not eligible for the Loan Rehabilitation Program a second time. If you are unable to pay your balance in full
						or maintain a satisfactory monthly payment, you may want to consider consolidating your loans through the Direct Consolidation Loan Program.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="10"
					title="What consequences can occur as a result of non-payment?"
				>
					<Typography>
						If payments are not made timely on your defaulted student loan, your guarantor (PHEAA) has the right to enact various nonpayment consequences against you.
					</Typography>
					<Typography component="ul">
						<Typography component="li">
							<Typography>
								<strong>Administrative Wage Garnishment Process</strong> - The administrative wage garnishment process allows your guarantor (PHEAA) to
								garnish up to 15% of your take home pay as a means of recouping the outstanding balance of your loan(s). Your guarantor will notify you, via mail at
								your last known address, at least 30 days prior to initiating administrative wage garnishment proceedings. Upon receipt of this notice, you have certain
								rights in accordance with federal laws.
							</Typography>
							<Typography>
								If you wish to establish a payment plan to avoid the initiation of the wage garnishment process or have questions concerning this nonpayment consequence,
								please contact us immediately at 1-800-233-0751.
							</Typography>
						</Typography>
						<Typography component="li">
							<Typography>
								<strong>Federal Treasury Offset Program</strong> - The Federal Treasury Offset program allows your guarantor (PHEAA) to seize federal monies you may have
								access to, such as tax returns, social security benefit payments, and travel reimbursements, to help in the repayment of your defaulted student loan debt.
							</Typography>
							<Typography>
								If you wish to establish a payment plan to avoid the Federal Tax Offset Program or have questions concerning this nonpayment consequence, please contact us
								immediately at 1-800-233-0751.
							</Typography>
						</Typography>
						<Typography component="li">
							<strong>The assignment of your loan to third party Collection Vendor</strong> - Your guarantor has the option of assigning collection for your loan to a
							third party collection vendor for assistance in recouping the defaulted loan balance.
						</Typography>
						<Typography component="li">
							<strong>The assignment of your loan to the United States Department of Education (USDE)</strong> - If it is determined that your loan is considered
							uncollectible, meaning that payments have not been made, your guarantor (PHEAA) may permanently assign your loan to the USDE for additional collection
							actions at the federal level. To avoid this nonpayment consequence, please contact us immediately at 1-800-233-0751 to discuss establishing a satisfactory repayment arrangement.
						</Typography>
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="11"
					title="How do I make a payment?"
				>
					<Typography>We offer several convenient ways for you to make a payment:</Typography>
					<Typography component="ul">
						<Typography component="li">
							<strong>Direct Debit</strong> - PHEAA offers free automatic deduction of your monthly payment from your checking or savings account.
							This repayment option allows you the freedom to choose how often your monthly payment is withdrawn (weekly, bi-weekly, etc.).
							To apply for Direct Debit, please complete, sign and return the <a href={replaceEnvTokenInUrl(`https://www${TOKEN_HOSTNAME_ENV}aessuccess.org/manage/pdf/default_debit.pdf`)} rel="noreferrer" target="_blank">Default Collections Agreement</a>.
						</Typography>
						<Typography component="li">
							<strong>Automated Clearing House (ACH) Debits</strong> - This option allows you to authorize a one-time debit entry from your checking or savings account.
						</Typography>
						<Typography component="li">
							<strong>Debit Card</strong> - You may authorize a single debit card payment to be applied to your account. Please note we do not accept credit cards as a form of payment.
						</Typography>
						<Typography component="li">
							<Typography gutterBottom={true}><strong>Mail</strong> - Mail your check or money order to:</Typography>
							<Typography fontWeight={700} gutterBottom={true} ml={2.5}>
								PHEAA<br />
								P.O. Box 1375<br />
								Buffalo, NY 14240-1375
							</Typography>
							<Typography>
								Be sure to include your account number on all payments made through the mail. After we receive your first payment, you will receive monthly billing
								statements if we have a valid mailing address on file.
							</Typography>
						</Typography>
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="12"
					title="How is my payment applied to my defaulted balance?"
				>
					<Typography>
						Your payment is first applied to outstanding charges, such as returned check fees, then to outstanding legal costs, then to outstanding collection fees,
						then to outstanding interest, and finally to outstanding principal.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="13"
					title="Can PHEAA take my tax return?"
				>
					<Typography gutterBottom={true}>
						Yes. If your account is certified for the Federal Treasury Offset Program any federal funds you may receive may be intercepted or seized.
						Federal funds could include, but are not limited to tax returns, social security benefit payments, federal pensions, travel reimbursements,
						Supplemental Security income, etc. This consequence can occur as early as 75 days in default.
					</Typography>
					<Typography>
						<a href={replaceEnvTokenInUrl(`https://www${TOKEN_HOSTNAME_ENV}pheaa.org/about/contact-us/default-collections.shtml`)}>Contact us</a> to get more information
						on how to avoid or inactivate this certification.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="14"
					title="Can PHEAA garnish my wages?"
				>
					<Typography gutterBottom={true}>
						Yes. Wage garnishment can occur as early as 75 days into default. If you do not begin to make voluntary payments by the date included in your 30 Day Prior
						to Wage Withholding Notice letter or email, your employer can be notified to begin involuntarily withholding up to 15% of your take home pay.
						Any garnishment payments received will be applied to the defaulted loan.
					</Typography>
					<Typography>
						<a href={replaceEnvTokenInUrl(`https://www${TOKEN_HOSTNAME_ENV}pheaa.org/about/contact-us/default-collections.shtml`)}>Contact us</a> so one of our loan
						counselors can help you avoid or educate you on how we can remove wage garnishment.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="15"
					title="Can PHEAA send me to an outside collection vendor?"
				>
					<Typography>
						Yes. PHEAA uses a series of outside collection vendors to assist with collection of the debt when voluntary payments are not being made.
						Once the account is with an outside collection agency, all payment arrangements will need to be made with them.
					</Typography>
					<TableContainerHelp>
						<Table size="small" aria-label="Collection Vendors and Phone Numbers">
							<TableHead>
								<TableRow>
									<TableCellHelpHead>Collection Vendor</TableCellHelpHead>
									<TableCellHelpHead>Phone Number</TableCellHelpHead>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow>
									<TableCellHelpBody>Windham Professionals</TableCellHelpBody>
									<TableCellHelpBody>1-877-752-9142</TableCellHelpBody>
								</TableRow>
								<TableRow>
									<TableCellHelpBody>GC Services</TableCellHelpBody>
									<TableCellHelpBody>1-866-841-9469</TableCellHelpBody>
								</TableRow>
								<TableRow>
									<TableCellHelpBody>Transworld Systems, Inc (TSI)</TableCellHelpBody>
									<TableCellHelpBody>1-866-744-1169</TableCellHelpBody>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainerHelp>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="16"
					title="What if my account is already assigned to an outside collection vendor?"
				>
					<Typography>
						If you know the name of the collection vendor handling your loan, contact them immediately and they will work with you to establish
						a satisfactory repayment arrangement. If you do not know the name of the collection vendor that is handling your loan, please
						contact our office at 1-800-233-0751 to obtain this information.
					</Typography>
					<TableContainerHelp>
						<Table size="small" aria-label="Collection Vendors and Phone Numbers">
							<TableHead>
								<TableRow>
									<TableCellHelpHead>Collection Vendor</TableCellHelpHead>
									<TableCellHelpHead>Phone Number</TableCellHelpHead>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow>
									<TableCellHelpBody>Windham Professionals</TableCellHelpBody>
									<TableCellHelpBody>1-877-752-9142</TableCellHelpBody>
								</TableRow>
								<TableRow>
									<TableCellHelpBody>GC Services</TableCellHelpBody>
									<TableCellHelpBody>1-866-841-9469</TableCellHelpBody>
								</TableRow>
								<TableRow>
									<TableCellHelpBody>Transworld Systems, Inc (TSI)</TableCellHelpBody>
									<TableCellHelpBody>1-866-744-1169</TableCellHelpBody>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainerHelp>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="17"
					title="What is the Direct Consolidation Loan Program?"
				>
					<Typography gutterBottom={true}>
						This is a program offered by the U.S. Department of Education that allows you to combine all of your eligible federal student loans into a single Direct Consolidation Loan.
					</Typography>
					<Typography>
						Some of the advantages of consolidation include:
					</Typography>
					<Typography component="ul">
						<Typography component="li">The removal of your loans from a defaulted status*</Typography>
						<Typography component="li">Reinstatement of your eligibility for future federal financial aid</Typography>
						<Typography component="li">Eligibility for applicable deferments, forbearances and repayment options</Typography>
					</Typography>
					<Typography>
						*Consolidation does not result in the removal of any negative credit reporting for your loan while it was in default or for information
						that may have been reported by your loan servicer prior to default.
					</Typography>
					<Typography>
						If you are interested in this program, please visit <a href="https://studentaid.gov/app/launchConsolidation.action" rel="noreferrer" target="_blank">StudentAid.gov</a> to get more information or contact the U.S. Department of Education at 1-800-557-7394.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="18"
					title="Will consolidating my loans get me out of default?"
				>
					<Typography gutterBottom={true}>
						Yes. If you proceed with consolidation, any outstanding collection fees on the account will be added to your balance. The only way to get a reduction in collection fees,
						and to remove any negative credit reporting, you should complete the Loan Rehabilitation Program.
					</Typography>
					<Typography>
						<a href={replaceEnvTokenInUrl(`https://www${TOKEN_HOSTNAME_ENV}pheaa.org/about/contact-us/default-collections.shtml`)}>Contact us</a> to discuss your options.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="19"
					title="What information should I have ready when completing my consolidation application?"
				>
					<Typography>
						You should have the following information with you when applying for consolidation:
					</Typography>
					<Typography component="ul">
						<Typography component="li">Demographic information for two references who do not live with you and who you have known for at least 5 years</Typography>
						<Typography component="li">Your driver's license number (if applicable)</Typography>
						<Typography component="li">Copy of your most recently filed tax return</Typography>
						<Typography component="li">Proof of your household monthly income</Typography>
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="20"
					title="Can I get financial aid while in default?"
				>
					<Typography>
						Yes. After you have remitted six on-time qualifying monthly payments, you may be eligible to receive additional Title IV eligibility for financial aid.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="21"
					title="Are there benefits for service members?"
				>
					<Typography>
						The Servicemembers Civil Relief Act (SCRA) is intended to grant servicemembers temporary relief from judicial and administrative proceedings and transactions.
						The SCRA limits the interest rate on a servicemember's eligible loans to a maximum of 6% while they are performing qualifying active duty military service.
						For the purposes of the SCRA, "interest" includes any other charges or fees applied to the loan. This benefit will not raise a servicemember's rate to 6% if
						it is already below 6%; instead, it provides a maximum rate. To obtain more information, please contact our office at 1-800-233-0751 or visit <a href="https://www.militaryonesource.mil" rel="noreferrer" target="_blank">www.militaryonesource.mil</a>.
					</Typography>
				</HelpModuleFAQItem>
			</Typography >
			<Typography component="h2" variant="h2" gutterBottom>Tax FAQ</Typography>
			<Typography className="cmp-help-module__faq" component="dl" mb={3}>
				<HelpModuleFAQItem
					questionId="tax01"
					title="What is a 1098-E IRS form?"
				>
					<Typography>
						This is the Student Loan Interest Statement that reports the amount of interest you paid on your student loans in a calendar year.
					</Typography>
					<Typography>
						<strong>NOTE:</strong> If you paid less than the summed eligible amount (in most cases $100), we will not mail you a 1098-E; however, you may view your tax information for the year online through Account Access.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax02"
					title="What is the 1098-E form used to communicate?"
				>
					<Typography>
						As your student loan servicer, we are required to furnish this statement to you if we received interest payments of $100 or more during the year on one or more qualified student loans.
					</Typography>
					<Typography>
						However, you may not be able to deduct the full amount of interest reported on this statement. For more information, do not contact your servicer, rather, <a href="https://www.irs.gov/pub/irs-pdf/p970.pdf" target="_blank" rel="noreferrer">see IRS Pub. 970 Tax Benefits for Education</a>, and the Student Loan Interest Deduction Worksheet in the Instructions for Forms 1040 and 1040-SR or contact your tax advisor.
					</Typography>
					<Typography>
						<strong>NOTE:</strong> If you paid less than the summed eligible amount (in most cases $100), we will not mail you a 1098-E; however, you may view your tax information for the year online through Account Access.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax03"
					title="What does my 1098-E form include?"
				>
					<Typography>
						For loans with a first disbursement before 9/1/2004, the 1098-E will include only interest paid within the tax calendar year being reported.
					</Typography>
					<Typography component="ul">
						For loans with a first disbursement on or after 9/1/2004, the 1098-E will include:
						<Typography component="li">Interest paid within the calendar year being reported</Typography>
						<Typography component="li">Eligible Origination Issue Discount (OID) satisfied by principal paid within the tax year being reported</Typography>
						<Typography component="li">Eligible Capped Interest satisfied by principal paid within the tax year being reported</Typography>
					</Typography>
					<Typography>
						<strong>NOTE:</strong> If you paid less than the summed eligible amount (in most cases $100), we will not mail you a 1098-E; however, you may view your tax information for the year online through Account Access.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax04"
					title="When will I receive my 1098-E?"
				>
					<Typography>
						All IRS reporting must be post marked or received electronically by midnight on January 31.  We ensure all tax information is available by the end of the third week in January each year.
					</Typography>
					<Typography>
						<strong>NOTE:</strong> If you paid less than the summed eligible amount (in most cases $100), we will not mail you a 1098-E; however, you may view your tax information for the year online through Account Access.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax05"
					title="If I am not the borrower will I receive a 1098-E?">
					<Typography>
						No, the 1098-E form is only sent to the primary borrower associated to the eligible loans being reported.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax06"
					title="How can I obtain another copy of my 1098-E?"
				>
					<Typography>
						You can obtain a copy by:
					</Typography>
					<Typography component="ul">
						<Typography component="li">Accessing your online account and viewing or printing your tax information</Typography>
						<Typography component="li">Contacting customer service and requesting a reprint of your tax information</Typography>
						<Typography component="li">Requesting a Tax Summary letter if you paid less than the summed eligible amount (in most cases $100).</Typography>
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax07"
					title="Whom can I contact if I have specific questions on tax filing questions associated to 1098-E?"
				>
					<Typography>
						All tax inquiries should be directed to a tax professional or the IRS at 1-800-829-1040 or <a href="https://irs.gov" target="_blank" rel="noreferrer">IRS.gov</a>.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax08"
					title="What happens if I don't receive a 1098-E form?"
				>
					<Typography>
						If you paid less than the summed eligible amount (in most cases $100), we will not mail you a 1098-E; however, you may view your tax information for the year online through Account Access.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax09"
					title="My loans just transferred to you. Will I get my tax statement from you and my previous servicer?"
				>
					<Typography>
						Yes, you will receive tax information from our office for the time period in which eligible payments were made and we serviced your loans.
					</Typography>
					<Typography>
						However, we are unable to provide tax information for a time period prior to your loans being transferred to us.
					</Typography>
					<Typography>
						You must contact your previous servicer to determine if you will receive a tax statement from them. If you made qualifying payments toward interest or had debt canceled with us, we will send you a tax statement.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax10"
					title="What is a 1099-C IRS form?"
				>
					<Typography>
						This is the Cancellation of Debt form that lists the amount of debt that was canceled on your student loans in a calendar year.
					</Typography>
					<Typography>
						<strong>NOTE:</strong> If you are the primary borrower and had the summed eligible amount (in most cases $100) of debt canceled, we will not mail you a 1099-C; however, you may view your tax information for the year online through Account Access.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax11"
					title="What is an IRS form 1099-C form used to communicate?"
				>
					<Typography>
						The IRS form 1099-C is used to communicate the summed eligible amount of discharges applied to a borrower's loan.  If you have any specific questions, you may want to contact your tax advisor.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax12"
					title="What does my 1099-C form include?"
				>
					<Typography>
						Your 1099-C form includes write off, charge off, and borrower benefits that are considered tax reportable transactions.  If you have any specific questions, you may want to contact your tax advisor.
					</Typography>
					<Typography>
						<strong>NOTE:</strong> As IRS federal law changes occur, eligible and reportable transactions may differ.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax13"
					title="When will I receive my 1099-C?"
				>
					<Typography>
						All IRS reporting must be post marked or received electronically by midnight on January 31. We ensure all tax information is available by the end of the third week in January each year.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax14"
					title="If I am not the borrower will I receive a 1099-C?"
				>
					<Typography>
						Yes, this is possible.
					</Typography>
					<Typography>
						This will occur when the summed eligible amount for the loans being reported is equal to or greater than $10,000 and an equally liable third party (co-signer or co-maker) is associated with a loan that was discharged.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax15"
					title="How can I obtain another copy of my 1099-C?"
				>
					<Typography>
						You can obtain a copy by:
					</Typography>
					<Typography component="ul">
						<Typography component="li">Accessing your online account and viewing or printing your tax information</Typography>
						<Typography component="li">Contacting customer service and requesting a reprint of your tax information</Typography>
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax16"
					title="Whom can I contact if I have specific questions on tax filing questions associated to 1099-C?"
				>
					<Typography>
						All tax inquiries should be directed to a tax professional or the IRS at 1-800-829-1040 or <a href="https://irs.gov" target="_blank" rel="noreferrer">IRS.gov</a>.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax17"
					title="What happens if I don't receive a 1099-C form?"
				>
					<Typography>
						If you had less than $100 debt canceled, we may not mail you a 1099-C; however, you may view your tax information for the year online through Account Access.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax18"
					title="My loans just transferred to you. Will I get my tax statement from you and my previous servicer?"
				>
					<Typography>
						Yes, you will receive tax information from our office for the time period in which student loan debt was canceled and we serviced your loans.
					</Typography>
					<Typography>
						However, we are unable to provide tax information for a time period prior to your loans being transferred to us.
					</Typography>
					<Typography>
						You must contact your previous servicer to determine if you will receive a tax statement from them.
					</Typography>
				</HelpModuleFAQItem>
				<HelpModuleFAQItem
					questionId="tax19"
					title="What if I made payments to or had debt discharged by both PHEAA and AES?"
				>
					<Typography>
						If you made qualifying payments or a discharge occurred by both PHEAA and AES, you may receive tax statements from both PHEAA and AES depending upon your summed eligible amount. However, your summed amount is always available through your PHEAA and <a href="https://access.aessuccess.org/sign-in" rel="noreferrer" target="_blank">AES portals.</a>
					</Typography>
				</HelpModuleFAQItem>
			</Typography>
		</React.Fragment >
	);
};

export default HelpModuleFAQ;
export { HelpModuleFAQHeader };